import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  BookOpen,
  Sparkles,
  Palette,
  Image,
  FolderOpen,
  Layers,
  Users,
  Settings,
  HelpCircle,
  MessageCircle,
  Mail,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Zap,
  Upload,
  Keyboard,
  AlertTriangle,
  Paintbrush,
  Camera,
  X,
} from 'lucide-react';
import Button from '../../components/Button';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Types                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

interface Article {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
}

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Knowledge Base Data                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

const categories = [
  { id: 'getting-started', label: 'Getting Started', icon: <BookOpen className="h-5 w-5" />, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { id: 'brand-team', label: 'Brand Team', icon: <Users className="h-5 w-5" />, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  { id: 'brand-craft', label: 'Brand Craft', icon: <Paintbrush className="h-5 w-5" />, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  { id: 'mockup-creation', label: 'Mockup Creation', icon: <Image className="h-5 w-5" />, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  { id: 'library', label: 'Library', icon: <FolderOpen className="h-5 w-5" />, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  { id: 'assets', label: 'Assets', icon: <Upload className="h-5 w-5" />, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' },
  { id: 'scenescaper', label: 'SceneScaper', icon: <Camera className="h-5 w-5" />, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200' },
  { id: 'your-brand', label: 'Your Brand', icon: <Palette className="h-5 w-5" />, color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-200' },
  { id: 'agency', label: 'Agency', icon: <Layers className="h-5 w-5" />, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  { id: 'account', label: 'Account & Billing', icon: <Settings className="h-5 w-5" />, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: <AlertTriangle className="h-5 w-5" />, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
];

const articles: Article[] = [
  /* ── Getting Started ─────────────────────────────────────── */
  {
    id: 'gs-welcome',
    title: 'Welcome to BrandScapes AI',
    category: 'getting-started',
    tags: ['intro', 'overview', 'welcome', 'first time'],
    content: `BrandScapes AI is a professional product mockup generator powered by Google Gemini AI. Whether you're a solo entrepreneur launching a brand, a designer creating client presentations, or an agency managing multiple brands — BrandScapes AI helps you create photorealistic mockups in minutes, not hours.

**What you can do:**
• **Create Mockups** — Choose from 22+ templates across categories like books, apparel, packaging, branding, and marketing materials. Upload your design, customize colors and effects, and generate professional mockups instantly.
• **Brand Team** — Launch a multi-agent AI workflow where 6 specialized agents collaborate to produce a full set of brand deliverables based on your brief.
• **Brand Craft** — Build a complete brand identity from scratch — name, colors, typography, logo, and preview mockups — all in one guided flow.
• **SceneScaper** — Generate lifestyle scenes featuring your brand assets using AI (Professional license required).
• **Library** — Save, organize, and manage all your generated mockups in one place.
• **Assets** — Upload and manage your brand assets (logos, images, designs) for use across all workflows.`,
  },
  {
    id: 'gs-first-mockup',
    title: 'Create Your First Mockup in 3 Steps',
    category: 'getting-started',
    tags: ['first mockup', 'quick start', 'tutorial', 'beginner', 'create'],
    content: `Creating your first mockup takes just a few minutes:

**Step 1 — Choose a Template**
Navigate to **Templates** from the sidebar. Browse categories (Books, Apparel, Branding, Marketing, etc.) or use the search bar to find what you need. Click on any template to begin.

**Step 2 — Upload & Customize**
Upload your primary design (logo, artwork, or product image). Supported formats: PNG, JPG, SVG, WebP (up to 10 MB). Then customize your mockup:
• Set background and product colors
• Toggle visual effects: shadow, reflection, glossy finish, emboss, gradient overlay
• Add product title and subtitle text
• Adjust scale and aspect ratio
• Write a custom prompt to guide the AI (optional but recommended)

**Step 3 — Generate & Download**
Click **Generate** and wait for the AI to create your mockup. Once ready, you can:
• Download in PNG or JPG format
• Save to your Library for later access
• Remove the background for a transparent PNG
• Regenerate a fresh variation with the same settings or adjust the setup first

**Pro tip:** Use the "Prompt Writing Tips" section on the Customize page for template-specific guidance on crafting effective AI prompts.`,
  },
  {
    id: 'gs-navigation',
    title: 'Navigating the Dashboard',
    category: 'getting-started',
    tags: ['navigation', 'sidebar', 'menu', 'dashboard', 'layout'],
    content: `Your dashboard is organized into clear sections in the left sidebar:

**Priority**
• **Brand Team** — AI-powered multi-agent brand workflow
• **Brand Craft** — Build a brand identity from scratch
• **Your Brand** — Store and manage your brand settings

**Workspace**
• **Templates** — Browse and select mockup templates
• **Library** — Access all your saved mockups
• **Assets** — Upload and manage brand images
• **SceneScaper** — Generate lifestyle scenes (Professional license)

**Business**
• **Agency** — Team and client management (Agency license)
• **Help & Support** — This Knowledge Base

The **Dashboard Home** provides a quick overview of your stats (total mockups, credits remaining, downloads, templates used) and recent mockups. It's your launchpad to any feature.`,
  },
  {
    id: 'gs-file-formats',
    title: 'Supported File Formats',
    category: 'getting-started',
    tags: ['formats', 'file types', 'png', 'jpg', 'svg', 'webp', 'upload', 'download'],
    content: `**Upload Formats**
You can upload designs and assets in the following formats:
• **PNG** — Best for logos and designs with transparency
• **JPG / JPEG** — Great for photographs and complex imagery
• **SVG** — Scalable vector graphics, ideal for logos
• **WebP** — Modern format with excellent compression

Maximum file size: **10 MB** for mockup creation and brand logo uploads.

**Download Formats**
Generated mockups can be downloaded in:
• **PNG** — Lossless quality, supports transparency (recommended)
• **JPG** — Smaller file size, white background replaces transparent areas

`,
  },

  /* ── Brand Team ──────────────────────────────────────────── */
  {
    id: 'bt-overview',
    title: 'What is Brand Team?',
    category: 'brand-team',
    tags: ['brand team', 'agents', 'ai workflow', 'overview'],
    content: `Brand Team is BrandScapes AI's flagship feature — a multi-agent AI workflow where 6 specialized agents collaborate to produce a complete set of brand deliverables from a single brief.

**The 6 AI Agents:**
• **Lena (Brand Manager)** — Manages the overall session, responds to your chat messages, and coordinates the team
• **Sofia (Brand Strategist)** — Creates your strategic brand brief based on your inputs
• **Reya (Asset Analyst)** — Analyzes your uploaded brand assets and user inputs
• **Max (Mockup Planner)** — Plans the deliverable queue and determines what to produce
• **Marco (Art Director)** — Defines visual direction, styling, and composition for each mockup
• **Kai (QA Reviewer)** — Reviews generated mockups for quality and brand consistency

You provide the brief, the agents handle everything else — from strategy to final deliverables.`,
  },
  {
    id: 'bt-wizard',
    title: 'Brand Team 5-Step Wizard',
    category: 'brand-team',
    tags: ['brand team', 'wizard', 'steps', 'workflow', 'setup', 'brief', 'plan', 'generate'],
    content: `The Brand Team workflow follows 5 clear steps:

**Step 1 — Setup**
Enter your business description and objectives. Then configure:
• **Pack Mode** — Choose from 12 specialized packs (Full Brand, E-Commerce, Packaging, Social Launch, Signage & Retail, Agency Presentation, Apparel & Merch, Corporate Identity, Food & Beverage, Book & Publishing, Event & Trade Show, Vouchers & Gifts)
• **Direction** — Set the creative tone: Safe (clean, commercial), Bold (high-contrast, assertive), or Premium (elevated, photorealistic)
• **Mockup Style** — Choose from 10 styles: Auto (AI Decides), Realism & Lifestyle, Contextual Scene, Hyper-Realistic AI, Human Interaction, Smart Dynamic, Brutalist & Editorial, Studio Clean, Flat Brand Color, or Minimal Scandinavian

**Step 2 — Brief**
The AI generates a strategic brand brief based on your inputs. Review it and approve to continue, or request changes.

**Step 3 — Plan**
A deliverable plan is generated showing exactly what mockups will be created. Approve the plan to proceed.

**Step 4 — Generate**
The agents work together to produce your mockups. You'll see real-time activity updates as each agent completes their tasks.

**Step 5 — Results**
View all generated mockups in a gallery. Preview individual results, download them individually, or download everything as a ZIP file.`,
  },
  {
    id: 'bt-sessions',
    title: 'Managing Brand Team Sessions',
    category: 'brand-team',
    tags: ['brand team', 'sessions', 'save', 'load', 'history'],
    content: `Brand Team sessions are automatically saved so you can return to them anytime.

**Session Features:**
• **Create New Session** — Start a fresh workflow with new inputs
• **Load Previous Session** — Pick up where you left off with any previous session
• **Switch Sessions** — Move between different brand projects seamlessly
• **Chat History** — All conversations with the Brand Manager agent are preserved within each session

**Tips:**
• Create separate sessions for different brands or campaigns
• Use the chat interface to ask Lena (Brand Manager) questions about your brand strategy or request modifications
• Sessions track the full audit trail of agent activity, so you can review what each agent did`,
  },
  {
    id: 'bt-pack-modes',
    title: 'Brand Team Pack Modes Explained',
    category: 'brand-team',
    tags: ['brand team', 'pack', 'mode', 'full brand', 'ecommerce', 'social', 'packaging'],
    content: `Each Pack Mode focuses on a specific set of deliverables:

• **Full Brand Pack** — Comprehensive cross-category set covering all brand surfaces
• **E-Commerce Pack** — Product listings, marketplace banners, and digital storefront assets
• **Packaging Pack** — Product packaging, labels, and box designs
• **Social Launch Pack** — Social media posts, stories, covers, and profile assets
• **Signage & Retail Pack** — Store signage, point-of-sale displays, and retail environments
• **Agency Presentation Pack** — Client-ready pitch decks and presentation mockups
• **Apparel & Merch Pack** — T-shirts, hoodies, caps, and merchandise mockups
• **Corporate Identity Pack** — Business cards, letterheads, envelopes, and stationery
• **Food & Beverage Pack** — Menu designs, packaging, and restaurant branding
• **Book & Publishing Pack** — Book covers, spine designs, and series presentations
• **Event & Trade Show Pack** — Booth displays, banners, badges, and event materials
• **Vouchers & Gifts Pack** — Gift cards, vouchers, coupons, and promotional items

Choose the pack that best matches your immediate needs. You can always run additional sessions with different packs.`,
  },

  /* ── Brand Craft ─────────────────────────────────────────── */
  {
    id: 'bc-overview',
    title: 'What is Brand Craft?',
    category: 'brand-craft',
    tags: ['brand craft', 'identity', 'logo', 'colors', 'typography', 'brand builder'],
    content: `Brand Craft is an AI-powered brand identity builder that helps you create a complete brand from scratch in 5 guided steps.

**What you'll create:**
• Brand name, tagline, and description
• Color palette (primary, secondary, accent)
• Typography pairing (heading + body fonts)
• AI-generated logo (4 variations to choose from)
• Preview mockups showing your brand applied to products

Brand Craft is perfect for new businesses, rebrands, or quickly exploring brand directions. The entire process takes 15–25 minutes.`,
  },
  {
    id: 'bc-steps',
    title: 'Brand Craft Step-by-Step Guide',
    category: 'brand-craft',
    tags: ['brand craft', 'steps', 'tutorial', 'color palette', 'fonts', 'logo generation'],
    content: `**Step 1 — Brand Identity**
Enter your brand name, tagline, and a description of your business. This information guides the AI across all subsequent steps.

**Step 2 — Color Palette**
Choose from 12 curated palettes (Royal Purple, Ocean Blue, Forest Green, Sunset Orange, Rose Pink, Midnight Dark, Earth Tones, Coral Reef, Steel Gray, Emerald Gold, Neon Cyber, Warm Sand) or create a custom palette by picking your own primary, secondary, and accent colors. A live preview strip shows your selections in real time.

**Step 3 — Typography**
Select from 12 professionally paired font combinations. Each pairing includes a display/heading font and a body font. Options range from modern (Inter, Space Grotesk) to elegant (Playfair Display, Lora) to bold (Montserrat, Bebas Neue). Fonts are loaded live so you can preview them instantly.

**Step 4 — Logo Generation**
The AI generates 4 logo variations based on your brand identity, colors, and typography. Select your favorite, or regenerate to get new options.

**Step 5 — Brand Preview**
See your brand applied to mockups (mug, business card, poster) and download the results. You can also save your brand settings for use across other BrandScapes AI features.`,
  },

  /* ── Mockup Creation ─────────────────────────────────────── */
  {
    id: 'mc-templates',
    title: 'Browsing and Choosing Templates',
    category: 'mockup-creation',
    tags: ['templates', 'browse', 'categories', 'select', 'mockup'],
    content: `Navigate to **Templates** from the sidebar to see all available mockup templates.

**Categories:**
• **Books** — Hardcover, paperback, and series presentations
• **Software** — Software boxes, CD cases, digital product boxes
• **Apparel** — T-shirts, hoodies, caps
• **Stands** — Banners, display booths, product stands
• **Branding** — Mugs, business cards, posters
• **Marketing** — Flyers, stickers, labels
• **Pro Scenes** — Premium professional scenes (Professional license required)

Use the **search bar** to find templates by name, or click a category to filter. Each template shows a description of what it produces. Click any template to start the creation workflow.`,
  },
  {
    id: 'mc-customize',
    title: 'Customizing Your Mockup',
    category: 'mockup-creation',
    tags: ['customize', 'effects', 'colors', 'text', 'prompt', 'shadow', 'reflection'],
    content: `After uploading your design, the Customize page gives you full control over how your mockup looks.

**Colors**
• **Background Color** — Click the color picker or type a description (e.g., "soft gradient blue")
• **Product Color** — Set the base color of the product itself

**Visual Effects**
• **Shadow** — Adds a natural drop shadow beneath the product
• **Reflection** — Creates a subtle reflection effect
• **Glossy Finish** — Applies a glossy/shiny surface effect
• **Emboss** — Gives the design a raised, textured look
• **Gradient Overlay** — Adds a smooth gradient over the product

**Text**
• **Product Title** — Main text displayed on or near the product
• **Product Subtitle** — Secondary text line

**Layout**
• **Scale** — Adjust how large your design appears on the product
• **Aspect Ratio** — Set the output image proportions

**Custom Prompt**
Write a description to guide the AI. For example: "Place the mug on a rustic wooden table with morning sunlight and steam rising from the cup." Use the **Prompt Writing Tips** dropdown for category-specific suggestions covering camera angles, lighting, styles, and settings.`,
  },
  {
    id: 'mc-prompts',
    title: 'Writing Effective AI Prompts',
    category: 'mockup-creation',
    tags: ['prompt', 'writing', 'tips', 'ai', 'gemini', 'quality', 'best practices'],
    content: `The custom prompt is the most powerful tool for controlling your mockup output. Here's how to write great prompts:

**Be Specific**
Instead of "nice background," try: "Clean white marble surface with soft natural window light from the left, slight bokeh in the background."

**Include Key Details:**
• **Camera angle** — "shot from above," "eye-level perspective," "45-degree angle"
• **Lighting** — "soft studio lighting," "golden hour sunlight," "dramatic side lighting"
• **Environment** — "minimalist desk setup," "outdoor café table," "rustic kitchen counter"
• **Mood** — "professional and clean," "warm and inviting," "bold and energetic"
• **Style** — "photorealistic," "editorial magazine style," "flat lay composition"

**Avoid:**
• Vague descriptions ("make it look good")
• Contradictory instructions ("dark and bright")
• Overly complex scenes with too many elements

**Template-Specific Tips:**
The Customize page includes a "Prompt Writing Tips" section with suggestions tailored to each template category. Expand it to see examples for your specific template.`,
  },
  {
    id: 'mc-download',
    title: 'Downloading and Saving Mockups',
    category: 'mockup-creation',
    tags: ['download', 'save', 'library', 'png', 'jpg', 'background removal', 'regenerate'],
    content: `After generation, you'll see your mockup on the Download page with several options:

**Download Options**
• Choose **PNG** (recommended, supports transparency) or **JPG** (smaller file size)
• Click **Download PNG** or **Download JPG** to save the current mockup directly
• If you generated a transparent version, use **Download Transparent PNG** for the cutout file

**Save to Library**
Click the "Save to Library" button to store your mockup permanently. Library mockups can be accessed anytime from the Library page.

**Remove Background**
Click "Remove Background" to get a transparent PNG version of your mockup — perfect for placing on custom backgrounds or in presentations.

**Regenerate**
Click "Regenerate Variations" to create a fresh result with the same settings, or use "Adjust Settings" when you want to change prompts, colors, or effects first.`,
  },

  /* ── Library ─────────────────────────────────────────────── */
  {
    id: 'lib-overview',
    title: 'Using the Mockup Library',
    category: 'library',
    tags: ['library', 'saved', 'mockups', 'organize', 'manage'],
    content: `The Library is where all your saved mockups live. Access it from the sidebar under Workspace.

**Viewing Options**
• Toggle between **Grid view** and **List view** using the icons in the top-right
• Use the **search bar** to find mockups by name
• Filter by **All mockups** or **Favorites only**
• Browse through pages using the pagination controls (12 items per page)

**For Each Mockup, You Can:**
• **Preview** — Open a full-screen preview with zoom (mouse scroll or +/- keys) and rotation (R key)
• **Download** — Save to your device as PNG
• **Favorite** — Mark important mockups with a heart icon for quick filtering
• **Remove Background** — Generate a transparent PNG version
• **Copy Link** — Copy the mockup URL to clipboard
• **Delete** — Permanently remove the mockup (with confirmation)

**Keyboard Shortcuts in Preview:**
• **ESC** — Close preview
• **+/-** — Zoom in/out
• **R** — Rotate 90°
• **D** — Download`,
  },

  /* ── Assets ──────────────────────────────────────────────── */
  {
    id: 'assets-overview',
    title: 'Managing Your Brand Assets',
    category: 'assets',
    tags: ['assets', 'upload', 'brand kit', 'images', 'organize', 'manage'],
    content: `The Assets page is your brand asset library — upload logos, product images, and design files to use across all BrandScapes AI workflows.

**Uploading Assets**
• Click the upload area or drag-and-drop files
• Supported formats: PNG, JPG, SVG, WebP
• Upload progress is tracked in real time
• Assets are automatically categorized

**Organizing**
• Switch between **Grid** and **List** views
• Filter by category using the dropdown
• Search by filename or file type
• Edit an asset's category with the inline editor

**Asset Actions**
• **Preview** — View the full-size image in a modal
• **Edit Category** — Reassign the asset to a different category
• **Remove Background** — AI-powered background removal
• **Download** — Save the original file to your device
• **Delete** — Permanently remove the asset

**Using Assets in Workflows**
When creating a mockup (Step 1 — Create), you can select any uploaded asset instead of uploading a new file. Assets are also available in SceneScaper and Brand Team workflows.`,
  },

  /* ── SceneScaper ─────────────────────────────────────────── */
  {
    id: 'ss-overview',
    title: 'What is SceneScaper?',
    category: 'scenescaper',
    tags: ['scenescaper', 'lifestyle', 'scenes', 'ai generation', 'professional'],
    content: `SceneScaper generates photorealistic lifestyle scenes featuring your brand assets using AI. It's designed for creating marketing imagery that shows your products in real-world contexts.

**Requirements:**
• **Professional or Agency license** — SceneScaper is a premium feature
• **2+ credits** — Each generation costs credits
• **At least one uploaded asset** — Select from your Brand Kit

**How to Use:**
1. Select an asset from the dropdown (search-enabled)
2. Write a **primary prompt** describing the scene (e.g., "A cozy coffee shop setting with morning light")
3. Optionally add a **negative prompt** to exclude elements (e.g., "no people, no text")
4. Choose your **aspect ratio** (1:1, 4:3, 16:9, etc.)
5. Toggle **style modifiers**: Photorealistic, Minimalist, Cinematic, Vintage Film, Vibrant & Colorful, Dark & Moody
6. Click **Generate** and wait for your scene

SceneScaper is ideal for social media content, website hero images, and marketing campaigns.`,
  },

  /* ── Your Brand ──────────────────────────────────────────── */
  {
    id: 'yb-overview',
    title: 'Setting Up Your Brand Profile',
    category: 'your-brand',
    tags: ['brand', 'profile', 'logo', 'colors', 'settings', 'brand identity'],
    content: `The Your Brand page stores your core brand settings that are used across all BrandScapes AI features.

**Brand Settings:**
• **Brand Name** — Your business or product name
• **Tagline** — A short brand tagline or slogan
• **Logo** — Upload your brand logo (PNG, JPG, SVG, max 10 MB). This logo can be auto-applied when creating mockups.
• **Primary Color** — Your main brand color
• **Secondary Color** — Supporting brand color
• **Accent Color** — Highlight or call-to-action color

**How It's Used:**
• When creating a mockup, you can check "Use Brand Logo" to automatically apply your saved logo
• Brand colors may be suggested as defaults in customization workflows
• Brand Craft can save its output here as your brand profile

**Tips:**
• Upload a high-resolution logo with a transparent background (PNG) for best results
• Keep your brand colors consistent — they'll be used to maintain visual coherence across mockups`,
  },

  /* ── Agency ──────────────────────────────────────────────── */
  {
    id: 'agency-overview',
    title: 'Agency Dashboard & Team Management',
    category: 'agency',
    tags: ['agency', 'team', 'clients', 'collaboration', 'management'],
    content: `The Agency dashboard is available with an Agency license and provides tools for team collaboration and client management.

**Four Tabs:**
• **Overview** — Agency stats at a glance: team members, active clients, mockups created, total revenue
• **Team Members** — Add and manage team collaborators with role-based access
• **Clients** — Track client projects and deliverables
• **Analytics** — Revenue and performance metrics

**Key Features:**
• Invite team members to collaborate on brand projects
• Organize work by client with separate project spaces
• Track mockup creation and download metrics per client
• View agency-wide performance analytics

**Getting Agency Access:**
Agency features require an Agency license. Upgrade from the Settings page or click the "Upgrade" badge next to Agency in the sidebar.`,
  },

  /* ── Account & Billing ───────────────────────────────────── */
  {
    id: 'acc-settings',
    title: 'Account Settings & Profile',
    category: 'account',
    tags: ['settings', 'account', 'profile', 'password', 'email', 'notifications'],
    content: `Access your account settings from the **Settings** page in the sidebar.

**Profile**
• Update your full name and company name
• View your email address and license level
• See your current credit balance

**Notifications**
• Toggle email updates on/off
• Control marketing email preferences
• Manage product news notifications

**Security**
• Change your password (requires current password)
• Password must meet minimum security requirements

**Account Actions**
• Upgrade your license (Free → Professional → Agency)
• Delete your account (permanent, with confirmation)`,
  },
  {
    id: 'acc-licenses',
    title: 'License Tiers & Features',
    category: 'account',
    tags: ['license', 'free', 'professional', 'agency', 'pricing', 'upgrade', 'plan', 'credits'],
    content: `BrandScapes AI offers three license tiers:

**Free**
• Access to Brand Team, Brand Craft, and mockup creation
• Browse all standard templates
• Save mockups to Library
• Upload and manage assets
• Limited credits

**Professional**
Everything in Free, plus:
• **SceneScaper** access — Generate lifestyle scenes
• **Pro Scene templates** — Premium, high-quality template collection
• More credits per month
• Priority generation queue

**Agency**
Everything in Professional, plus:
• **Agency Dashboard** — Full team and client management
• **Team collaboration** — Invite team members
• **Client management** — Organize work by client
• **Analytics** — Revenue and performance tracking
• Maximum credits allocation

**Upgrading:**
Go to **Settings** and click "Upgrade License," or click any "Upgrade" badge throughout the app.`,
  },
  {
    id: 'acc-credits',
    title: 'Understanding Credits',
    category: 'account',
    tags: ['credits', 'usage', 'remaining', 'generation', 'cost'],
    content: `Credits are used when generating mockups and AI content across BrandScapes AI.

**How Credits Work:**
• Each mockup generation uses credits
• SceneScaper generations require 2+ credits
• Brand Team sessions use credits based on the number of deliverables generated
• Credits refresh based on your license tier

**Checking Your Balance:**
• View your remaining credits on the Dashboard Home (Stats section)
• The Settings page also displays your current credit balance

**Running Low?**
If you're running low on credits, consider upgrading your license tier for a higher monthly allocation.`,
  },

  /* ── Troubleshooting ─────────────────────────────────────── */
  {
    id: 'ts-generation',
    title: 'Mockup Generation Issues',
    category: 'troubleshooting',
    tags: ['error', 'generation', 'failed', 'stuck', 'loading', 'not working'],
    content: `**Mockup not generating or stuck loading?**
• Check your credit balance — you need available credits to generate
• Ensure your uploaded image meets format requirements (PNG, JPG, SVG, WebP, under 10 MB)
• Try a simpler prompt if the AI is having trouble with a complex scene
• Refresh the page and try again

**Generation failed with an error?**
• "Mockup expired" — The generated file was temporary and has been cleaned up. Go back to Customize and regenerate.
• "Upload failed" — Check your file size (max 10 MB) and format
• Network errors — Check your internet connection and try again

**Quality not as expected?**
• Write more specific prompts — include camera angle, lighting, and environment details
• Use the Prompt Writing Tips section on the Customize page
• Try different visual effects combinations
• Regenerate with the same settings — AI output varies each time`,
  },
  {
    id: 'ts-download',
    title: 'Download Problems',
    category: 'troubleshooting',
    tags: ['download', 'failed', 'not working', 'save', 'error'],
    content: `**Download button not working?**
• The file may have expired. Generated mockups are temporary — save to Library for permanent access.
• Check your browser's download settings and popup blocker
• Try a different format (switch between PNG and JPG)

**Downloaded file looks wrong?**
• JPG format does not support transparency — transparent areas appear white. Use PNG if you need transparency.
• If the image still looks soft after a new download, regenerate a fresh variation and compare outputs
• For layered design work or compositing, use the transparent PNG option after background removal

**Can't save to Library?**
• Ensure you're logged in
• Check if the mockup has expired — you may need to regenerate it first`,
  },
  {
    id: 'ts-auth',
    title: 'Login & Authentication Issues',
    category: 'troubleshooting',
    tags: ['login', 'auth', 'password', 'forgot', 'locked out', 'sign in'],
    content: `**Can't log in?**
• Double-check your email and password
• Use the "Forgot Password" link on the login page to reset your password
• Clear your browser cache and cookies, then try again
• Try using a different browser

**Session expired?**
• For security, sessions expire after a period of inactivity
• Simply log in again — your data is safely stored

**Cache issues?**
If the app behaves unexpectedly after login:
1. Click the "Clear Cache" button on the login page
2. Or clear your browser cache manually
3. Hard refresh with Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac)`,
  },
  {
    id: 'ts-brand-team',
    title: 'Brand Team Troubleshooting',
    category: 'troubleshooting',
    tags: ['brand team', 'stuck', 'agents', 'not responding', 'error', 'timeout'],
    content: `**Agents seem stuck or not responding?**
• The Brand Team workflow polls for updates every 4 seconds. Wait a moment for the status to refresh.
• If an agent has been "in progress" for more than 2 minutes, try refreshing the page
• Complex briefs may take longer to process — the Full Brand Pack generates the most deliverables

**Brief or Plan looks wrong?**
• You can request changes before approving — use the chat with Lena (Brand Manager) to discuss modifications
• If the brief doesn't capture your intent, try rewriting your business description with more specific details

**Generation quality issues?**
• Select a more specific Pack Mode that matches your needs
• Use the "Premium" direction for highest-quality photorealistic outputs
• The "Hyper-Realistic AI" mockup style produces the most detailed renders`,
  },
];

const faqs: FAQ[] = [
  { question: 'How do I create my first mockup?', answer: 'Go to Templates from the sidebar, pick a template, upload your design, customize colors and effects, then click Generate. Your mockup will be ready in seconds. Download it or save to Library.', category: 'getting-started' },
  { question: 'What file formats can I upload?', answer: 'PNG, JPG, SVG, and WebP files up to 10 MB for mockup creation. Brand logo uploads also accept PNG, JPG, and SVG files up to 10 MB.', category: 'getting-started' },
  { question: 'What formats can I download mockups in?', answer: 'PNG (with transparency support) or JPG. The download page also lets you create a transparent PNG and regenerate a fresh variation with the same settings.', category: 'getting-started' },
  { question: 'Can I use mockups for commercial purposes?', answer: 'Yes. All mockups created with BrandScapes AI can be used commercially without attribution.', category: 'account' },
  { question: 'What is Brand Team and how does it work?', answer: 'Brand Team is a multi-agent AI workflow with 6 specialized agents that collaboratively produce brand deliverables. Set up a brief, approve the strategy, and the agents generate a complete set of mockups automatically.', category: 'brand-team' },
  { question: 'What are the Pack Modes in Brand Team?', answer: 'Pack Modes define the type of deliverables generated. Choose from 12 packs: Full Brand, E-Commerce, Packaging, Social Launch, Signage & Retail, Agency Presentation, Apparel & Merch, Corporate Identity, Food & Beverage, Book & Publishing, Event & Trade Show, and Vouchers & Gifts.', category: 'brand-team' },
  { question: 'What is Brand Craft?', answer: 'Brand Craft is an AI-powered brand identity builder. In 5 steps, you define your brand name, choose colors and typography, generate a logo, and preview your brand on mockups.', category: 'brand-craft' },
  { question: 'What is SceneScaper?', answer: 'SceneScaper generates AI-powered lifestyle scenes featuring your brand assets. It requires a Professional or Agency license and uses 2+ credits per generation.', category: 'scenescaper' },
  { question: 'How do credits work?', answer: 'Credits are consumed when generating mockups and AI content. Your balance refreshes based on your license tier. View remaining credits on the Dashboard or Settings page.', category: 'account' },
  { question: 'How do I upgrade my license?', answer: 'Go to Settings and click "Upgrade License," or click any "Upgrade" badge in the sidebar. Choose Professional for SceneScaper and Pro templates, or Agency for team collaboration features.', category: 'account' },
  { question: 'What\'s the difference between Free, Professional, and Agency?', answer: 'Free includes core features (mockups, Brand Team, Brand Craft, Library). Professional adds SceneScaper, Pro templates, and more credits. Agency adds team management, client tracking, and analytics.', category: 'account' },
  { question: 'How do I save my brand colors and logo?', answer: 'Go to Your Brand in the sidebar. Upload your logo, set your primary/secondary/accent colors, and click Save. These settings are used across mockup creation workflows.', category: 'your-brand' },
  { question: 'Can I remove the background from a mockup?', answer: 'Yes. On the Download page after generation, click "Remove Background" for a transparent PNG. You can also remove backgrounds from Library mockups and Assets.', category: 'library' },
  { question: 'My mockup generation failed. What should I do?', answer: 'Check your credit balance and file format. Try a simpler prompt or different effects. If the error says "expired," regenerate from the Customize page. Persistent issues? Contact support.', category: 'troubleshooting' },
  { question: 'How do I cancel my subscription?', answer: 'Go to Settings and look for subscription management options. Your access continues until the end of your current billing period.', category: 'account' },
  { question: 'Is my data secure?', answer: 'Yes. BrandScapes AI uses secure authentication, encrypted connections, and role-based access controls. Your uploads and generated mockups are private to your account.', category: 'account' },
];

/* ────────────────────────────────────────────────────────────────────────── */
/*  Component                                                                */
/* ────────────────────────────────────────────────────────────────────────── */

const Support: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [expandedFaqs, setExpandedFaqs] = useState<Set<number>>(new Set());
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Focus search on Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search logic
  const normalizedQuery = searchQuery.toLowerCase().trim();

  const filteredArticles = useMemo(() => {
    let results = articles;
    if (activeCategory) {
      results = results.filter((a) => a.category === activeCategory);
    }
    if (normalizedQuery) {
      results = results.filter(
        (a) =>
          a.title.toLowerCase().includes(normalizedQuery) ||
          a.content.toLowerCase().includes(normalizedQuery) ||
          a.tags.some((t) => t.includes(normalizedQuery))
      );
    }
    return results;
  }, [activeCategory, normalizedQuery]);

  const filteredFaqs = useMemo(() => {
    let results = faqs;
    if (activeCategory) {
      results = results.filter((f) => f.category === activeCategory);
    }
    if (normalizedQuery) {
      results = results.filter(
        (f) =>
          f.question.toLowerCase().includes(normalizedQuery) ||
          f.answer.toLowerCase().includes(normalizedQuery)
      );
    }
    return results;
  }, [activeCategory, normalizedQuery]);

  const displayedFaqs = showAllFaqs || normalizedQuery || activeCategory ? filteredFaqs : filteredFaqs.slice(0, 6);

  const activeCategoryData = categories.find((c) => c.id === activeCategory);

  const toggleFaq = (index: number) => {
    setExpandedFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const clearFilters = () => {
    setActiveCategory(null);
    setSearchQuery('');
    setExpandedArticle(null);
  };

  // Render markdown-style bold in content
  const renderContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.trim() === '') return <br key={i} />;

      // Process bold markers
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
        }
        return <span key={j}>{part}</span>;
      });

      // Bullet points
      if (line.trimStart().startsWith('•')) {
        return (
          <div key={i} className="flex gap-2 pl-1">
            <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
            <span className="flex-1">{rendered.map((r, k) => <React.Fragment key={k}>{r}</React.Fragment>)}</span>
          </div>
        );
      }

      // Numbered steps
      const numMatch = line.match(/^(\d+)\.\s/);
      if (numMatch) {
        return (
          <div key={i} className="flex gap-3 pl-1">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-purple/10 text-xs font-semibold text-brand-purple">
              {numMatch[1]}
            </span>
            <span className="flex-1 pt-0.5">{rendered.map((r, k) => <React.Fragment key={k}>{r}</React.Fragment>)}</span>
          </div>
        );
      }

      return <p key={i}>{rendered.map((r, k) => <React.Fragment key={k}>{r}</React.Fragment>)}</p>;
    });
  };

  return (
    <div className="p-4 md:p-8">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="mb-8">
        <h1 className="mb-2 font-heading text-h2 text-text-dark">Knowledge Base</h1>
        <p className="text-body text-body-grey">
          Everything you need to know about BrandScapes AI — search or browse by topic.
        </p>
      </div>

      {/* ── Search Bar ──────────────────────────────────────── */}
      <div className="relative mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles, guides, and FAQs..."
            className="w-full rounded-xl border border-gray-200 bg-white py-3.5 pl-12 pr-24 text-base shadow-sm transition-all placeholder:text-gray-400 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
          />
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <kbd className="hidden rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-400 sm:inline-block">
              Ctrl+K
            </kbd>
          </div>
        </div>
        {normalizedQuery && (
          <p className="mt-2 text-sm text-gray-500">
            Found {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} and{' '}
            {filteredFaqs.length} FAQ{filteredFaqs.length !== 1 ? 's' : ''}
            {activeCategory && activeCategoryData ? ` in ${activeCategoryData.label}` : ''}
          </p>
        )}
      </div>

      {/* ── Quick Contact Banner ────────────────────────────── */}
      <div className="mb-8 rounded-xl bg-gradient-to-r from-brand-purple to-brand-blue p-6 text-white md:p-8">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div>
            <h2 className="mb-1 font-heading text-h3">Can't find what you need?</h2>
            <p className="text-sm opacity-90 md:text-base">Our support team typically responds within a few hours.</p>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" size="medium" href="https://www.clawbrandai.com/support">
              <MessageCircle className="mr-2 h-4 w-4" />
              Contact Us
            </Button>
            <Button variant="secondary" size="medium" href="mailto:support@clawbrandai.com">
              <Mail className="mr-2 h-4 w-4" />
              Email Support
            </Button>
          </div>
        </div>
      </div>

      {/* ── Category Pills ──────────────────────────────────── */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={clearFilters}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all ${
              !activeCategory
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            All Topics
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(activeCategory === cat.id ? null : cat.id);
                setExpandedArticle(null);
              }}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Quick Links (only when no search/filter active) ── */}
      {!normalizedQuery && !activeCategory && (
        <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/dashboard/mockups"
            className="group flex items-center gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all hover:shadow-md hover:ring-brand-purple/30"
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100">
              <Zap className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900">Quick Start</h3>
              <p className="text-sm text-gray-500">Create your first mockup</p>
            </div>
            <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-brand-purple" />
          </Link>
          <Link
            to="/dashboard/brand-team"
            className="group flex items-center gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all hover:shadow-md hover:ring-brand-purple/30"
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 transition-colors group-hover:bg-violet-100">
              <Users className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900">Brand Team</h3>
              <p className="text-sm text-gray-500">AI multi-agent workflow</p>
            </div>
            <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-brand-purple" />
          </Link>
          <Link
            to="/dashboard/brand-craft"
            className="group flex items-center gap-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all hover:shadow-md hover:ring-brand-purple/30"
          >
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
              <Paintbrush className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900">Brand Craft</h3>
              <p className="text-sm text-gray-500">Build a brand identity</p>
            </div>
            <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-brand-purple" />
          </Link>
        </div>
      )}

      <div ref={contentRef}>
        {/* ── Articles ────────────────────────────────────────── */}
        <section className="mb-10">
          <h2 className="mb-5 flex items-center gap-2 text-lg font-semibold text-text-dark">
            <BookOpen className="h-5 w-5 text-brand-purple" />
            {activeCategory && activeCategoryData ? activeCategoryData.label : 'Guides & Articles'}
            <span className="ml-1 text-sm font-normal text-gray-400">({filteredArticles.length})</span>
          </h2>

          {filteredArticles.length === 0 ? (
            <div className="rounded-xl bg-gray-50 p-8 text-center">
              <Search className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="font-medium text-gray-500">No articles found</p>
              <p className="mt-1 text-sm text-gray-400">Try a different search term or browse all topics.</p>
              {(normalizedQuery || activeCategory) && (
                <button onClick={clearFilters} className="mt-3 text-sm font-medium text-brand-purple hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredArticles.map((article) => {
                const catData = categories.find((c) => c.id === article.category);
                const isExpanded = expandedArticle === article.id;

                return (
                  <div
                    key={article.id}
                    className={`rounded-xl bg-white shadow-sm ring-1 transition-all ${
                      isExpanded ? 'ring-brand-purple/30 shadow-md' : 'ring-gray-100 hover:ring-gray-200'
                    }`}
                  >
                    <button
                      onClick={() => setExpandedArticle(isExpanded ? null : article.id)}
                      className="flex w-full items-center gap-3 p-5 text-left"
                    >
                      {catData && (
                        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${catData.bg} ${catData.color}`}>
                          {catData.icon}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-900">{article.title}</h3>
                        {!isExpanded && (
                          <p className="mt-0.5 truncate text-sm text-gray-500">
                            {article.content.slice(0, 120).replace(/\*\*/g, '')}...
                          </p>
                        )}
                      </div>
                      <ChevronDown
                        className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-4">
                        <div className="prose-sm max-w-none space-y-2 text-gray-600 leading-relaxed">
                          {renderContent(article.content)}
                        </div>
                        {!activeCategory && catData && (
                          <div className="mt-4 flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 rounded-full ${catData.bg} px-2.5 py-1 text-xs font-medium ${catData.color}`}>
                              {catData.icon}
                              {catData.label}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── FAQs ────────────────────────────────────────────── */}
        <section className="mb-10">
          <h2 className="mb-5 flex items-center gap-2 text-lg font-semibold text-text-dark">
            <HelpCircle className="h-5 w-5 text-brand-purple" />
            Frequently Asked Questions
            <span className="ml-1 text-sm font-normal text-gray-400">({filteredFaqs.length})</span>
          </h2>

          {filteredFaqs.length === 0 ? (
            <div className="rounded-xl bg-gray-50 p-8 text-center">
              <HelpCircle className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="font-medium text-gray-500">No matching FAQs</p>
              <p className="mt-1 text-sm text-gray-400">Try a different search term.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {displayedFaqs.map((faq, index) => {
                  const isOpen = expandedFaqs.has(index);
                  return (
                    <div
                      key={index}
                      className={`rounded-xl bg-white shadow-sm ring-1 transition-all ${
                        isOpen ? 'ring-brand-purple/20' : 'ring-gray-100'
                      }`}
                    >
                      <button
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center gap-3 p-4 text-left"
                      >
                        <HelpCircle className={`h-5 w-5 flex-shrink-0 transition-colors ${isOpen ? 'text-brand-purple' : 'text-gray-400'}`} />
                        <span className="flex-1 font-medium text-gray-900">{faq.question}</span>
                        <ChevronRight
                          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <p className="pl-8 text-sm leading-relaxed text-gray-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {!normalizedQuery && !activeCategory && filteredFaqs.length > 6 && (
                <button
                  onClick={() => setShowAllFaqs(!showAllFaqs)}
                  className="mt-4 flex items-center gap-1 text-sm font-medium text-brand-purple hover:underline"
                >
                  {showAllFaqs ? 'Show less' : `Show all ${filteredFaqs.length} FAQs`}
                  <ChevronDown className={`h-4 w-4 transition-transform ${showAllFaqs ? 'rotate-180' : ''}`} />
                </button>
              )}
            </>
          )}
        </section>
      </div>

      {/* ── Keyboard Shortcuts Reference ─────────────────────── */}
      <section className="mb-10">
        <h2 className="mb-5 flex items-center gap-2 text-lg font-semibold text-text-dark">
          <Keyboard className="h-5 w-5 text-brand-purple" />
          Keyboard Shortcuts
        </h2>
        <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { keys: 'Ctrl + K', action: 'Search Knowledge Base' },
              { keys: 'ESC', action: 'Close preview / Clear search' },
              { keys: '+  /  -', action: 'Zoom in / out (preview)' },
              { keys: 'R', action: 'Rotate image (preview)' },
              { keys: 'D', action: 'Download image (preview)' },
            ].map((shortcut, i) => (
              <div key={i} className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-600">{shortcut.action}</span>
                <kbd className="flex-shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-500">
                  {shortcut.keys}
                </kbd>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Still Need Help ──────────────────────────────────── */}
      <section className="rounded-xl border border-gray-100 bg-gradient-to-r from-brand-purple/5 to-brand-blue/5 p-6 md:p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-purple/10">
            <Mail className="h-6 w-6 text-brand-purple" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Still have questions?</h3>
          <p className="mx-auto mb-5 max-w-md text-sm text-gray-500">
            Our team is here to help. Reach out and we'll get back to you as quickly as possible.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="primary" size="medium" href="mailto:support@clawbrandai.com">
              <Mail className="mr-2 h-4 w-4" />
              support@clawbrandai.com
            </Button>
            <Button variant="secondary" size="medium" href="https://www.clawbrandai.com/support">
              <MessageCircle className="mr-2 h-4 w-4" />
              Contact Form
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
